import React, { useState } from 'react';
import { Todo } from '../types/Todo';

interface TodoItemProps {
  todo: Todo;
  onToggle: (id: string) => void;
  onUpdate: (id: string, updates: Partial<Todo>) => void;
  onDelete: (id: string) => void;
}

const TodoItem: React.FC<TodoItemProps> = ({ todo, onToggle, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    title: todo.title,
    description: todo.description || '',
    priority: todo.priority,
    category: todo.category,
    dueDate: todo.dueDate || ''
  });

  const handleSave = () => {
    onUpdate(todo.id, editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData({
      title: todo.title,
      description: todo.description || '',
      priority: todo.priority,
      category: todo.category,
      dueDate: todo.dueDate || ''
    });
    setIsEditing(false);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'Overdue';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return `${diffDays} days left`;
  };

  const isOverdue = todo.dueDate && new Date(todo.dueDate) < new Date() && !todo.completed;

  return (
    <div className={`card animate-slide-up group hover:shadow-2xl transition-all duration-300 ${todo.completed ? 'opacity-75 bg-gray-50/80' : 'bg-white/95 hover:bg-white'} ${isOverdue ? 'border-red-300 bg-red-50/80 ring-2 ring-red-200' : 'border-white/20 hover:border-blue-200'}`}>
      {isEditing ? (
        <div className="space-y-4">
          <input
            type="text"
            value={editData.title}
            onChange={(e) => setEditData({ ...editData, title: e.target.value })}
            className="input-field"
            placeholder="Task title"
          />
          <textarea
            value={editData.description}
            onChange={(e) => setEditData({ ...editData, description: e.target.value })}
            className="input-field resize-none"
            rows={3}
            placeholder="Task description (optional)"
          />
          <div className="grid grid-cols-2 gap-4">
            <select
              value={editData.priority}
              onChange={(e) => setEditData({ ...editData, priority: e.target.value as any })}
              className="input-field"
            >
              <option value="low">Low Priority</option>
              <option value="medium">Medium Priority</option>
              <option value="high">High Priority</option>
            </select>
            <input
              type="text"
              value={editData.category}
              onChange={(e) => setEditData({ ...editData, category: e.target.value })}
              className="input-field"
              placeholder="Category"
            />
          </div>
          <input
            type="date"
            value={editData.dueDate}
            onChange={(e) => setEditData({ ...editData, dueDate: e.target.value })}
            className="input-field"
          />
          <div className="flex gap-2">
            <button onClick={handleSave} className="btn-primary">
              Save
            </button>
            <button onClick={handleCancel} className="btn-secondary">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <button
              onClick={() => onToggle(todo.id)}
              aria-label={todo.completed ? `Mark ${todo.title} as incomplete` : `Mark ${todo.title} as complete`}
              className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 transform hover:scale-110 ${todo.completed
                ? 'bg-gradient-to-r from-green-500 to-green-600 border-green-500 text-white shadow-lg'
                : 'border-gray-300 hover:border-green-500 hover:bg-green-50 group-hover:border-green-400'
                }`}
            >
              {todo.completed && (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className={`font-semibold text-lg leading-tight ${todo.completed ? 'line-through text-gray-500' : 'text-gray-900 group-hover:text-blue-900'} transition-colors duration-200`}>
                    {todo.title}
                  </h3>
                  {todo.description && (
                    <p className={`mt-2 text-sm leading-relaxed ${todo.completed ? 'text-gray-400' : 'text-gray-600 group-hover:text-gray-700'} transition-colors duration-200`}>
                      {todo.description}
                    </p>
                  )}
                </div>
                {isOverdue && (
                  <div className="ml-4 flex-shrink-0">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200 animate-pulse">
                      ⚠️ Overdue
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <span className={`px-3 py-1.5 rounded-full border text-sm font-medium ${getPriorityColor(todo.priority)} shadow-sm`}>
              {todo.priority === 'high' && '🔴'}
              {todo.priority === 'medium' && '🟡'}
              {todo.priority === 'low' && '🟢'}
              {todo.priority.charAt(0).toUpperCase() + todo.priority.slice(1)} Priority
            </span>
            <span className="px-3 py-1.5 bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 rounded-full border border-blue-300 text-sm font-medium shadow-sm">
              📁 {todo.category}
            </span>
            {todo.dueDate && (
              <span className={`px-3 py-1.5 rounded-full border text-sm font-medium shadow-sm ${isOverdue
                ? 'bg-gradient-to-r from-red-100 to-red-200 text-red-800 border-red-300'
                : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 border-gray-300'
                }`}>
                📅 {formatDate(todo.dueDate)}
              </span>
            )}
          </div>

          <div className="flex gap-3 pt-3 border-t border-gray-100 group-hover:border-gray-200 transition-colors duration-200">
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-3 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg text-sm font-medium transition-all duration-200 transform hover:scale-105"
              aria-label={`Edit task: ${todo.title}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </button>
            <button
              onClick={() => onDelete(todo.id)}
              className="flex items-center gap-2 px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg text-sm font-medium transition-all duration-200 transform hover:scale-105"
              aria-label={`Delete task: ${todo.title}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TodoItem;
